/**
 * F17 T17.1.5 — Generic async MultiSelect primitive.
 *
 * Memory: 2026-05-25 — brak reusable MultiSelect w admin SPA (TagInput istnieje
 * ale to entry, nie multi-select z dropdownem). Plan T17.1.5 wymaga dla:
 * - UserMultiSelectField (akceptanci typu `specific_users`)
 * - RoleMultiSelectField (akceptanci typu `roles`)
 *
 * UX:
 * - Wybrane wartości jako chip'y z X (osobny button per chip — usuwa zagnieżdżenie
 *   button w button, fix dla HTML invalid + WCAG 2.1.1 keyboard)
 * - Click trigger → otwiera dropdown z `options`
 * - Type w input → filtruje opcje (search z diakrytyką-insensitive)
 * - Click opcji → toggle (add/remove)
 * - Esc / click outside → zamyka dropdown
 * - Klawiatura: Enter/Space na triggerze otwiera dropdown
 * - Opcjonalnie (`showBulkActions`): pasek „Zaznacz wszystko / Odznacz wszystko" pod
 *   polem Szukaj — działa na WIDOCZNE opcje (po wyszukaniu: tylko na wyniki)
 *
 * a11y: outer to `<div role="combobox">` zamiast button (chipy mogą być
 * interactive child); listbox dropdown z `role="listbox"` + `role="option"`.
 *
 * i18n: user-facing stringi (placeholder, searchPlaceholder, noResults,
 * removeAriaLabel, selectAll, deselectAll) z `common.multiSelect.*` per CLAUDE.md §4.5.
 */

import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { ChevronDown, X } from 'lucide-react';

import { Button } from '@/components/Button';
import { type AnchoredPanelPosition, computeAnchoredPanelPosition } from '@/lib/anchored-panel';
import { normalizeSearch } from '@/lib/search-normalize';
import { cn } from '@/lib/utils';

export interface MultiSelectOption {
  /** Unikalna wartość (UUID typowo). */
  value: string;
  /** Wyświetlana etykieta. */
  label: string;
  /** Opcjonalny pomocniczy tekst pod labelem (np. email pod nazwą). */
  description?: string;
  /**
   * Opcjonalny kolor pozycji (hex) — renderowany jako kropka na chipie i w liście opcji.
   * Konsument: grupy ze słownika (pozycje mają kolor z palety). Brak koloru = bez kropki,
   * czyli zachowanie identyczne jak przed dodaniem tego pola.
   */
  color?: string | null;
}

export interface MultiSelectProps {
  /** Aktualnie wybrane values (controlled). */
  value: ReadonlyArray<string>;
  /** Callback przy zmianie selekcji (nowa lista values). */
  onChange: (value: string[]) => void;
  /** Lista wszystkich dostępnych opcji. */
  options: ReadonlyArray<MultiSelectOption>;
  /** Placeholder gdy nic nie wybrane. Default: `common.multiSelect.placeholder`. */
  placeholder?: string;
  /** Aria-label / wizualny label nad inputem. */
  label?: string;
  /** Pokazuje gwiazdkę przy label (visual). Walidacja po stronie konsumenta. */
  required?: boolean;
  /** Disabled state (np. gdy parent ma loading). */
  disabled?: boolean;
  /** Klasy dla wrapper'a. */
  className?: string;
  /**
   * Dodatkowe klasy kontenera WARTOŚCI (chipy albo placeholder) w triggerze — np. wyrównanie
   * wartości w komórce tabeli (`justify-center` / `justify-end`). Gdy podane, wartość trafia do
   * osobnego kontenera `flex min-w-0 flex-1 flex-wrap items-center gap-1`, który zajmuje miejsce
   * przed strzałką; bez tego propa chipy leżą bezpośrednio w triggerze, jak dotąd (wygląd i
   * struktura bez zmian).
   */
  valueClassName?: string;
  /** Test-id na wrapperze + bazowy prefix dla chipów/opcji wewnątrz. */
  'data-testid'?: string;
  /**
   * Wywoływany przy KAŻDEJ zmianie stanu otwarcia dropdownu (Etap 10.3, ADR-0066
   * §11 — review fix). Konsumenci ze słownikiem zewnętrznym param-bound
   * (`dictionaryParamBindings`) używają tego do uzbrajania/rozbrajania fetchu
   * opcji ("fetch-on-open") zamiast owijać `MultiSelect` w `<div onFocus/onBlur>`
   * łapiący bąbelkujący focus/blur — ten wzorzec był niewiarygodny: zamknięcie
   * dropdownu ODMONTOWUJE wewnętrzny `<input autoFocus>` search boxa, a
   * blur/focusout usuwanego z DOM elementu w praktyce NIE bąbelkuje do
   * przodków (żadnego focusin nie ma czym „dogonić" — węzeł już nie ma
   * rodzica w drzewie w momencie wysyłki zdarzenia), więc wrapper zostawał
   * uzbrojony NA STAŁE po pierwszym otwarciu. `isOpen` jest jedynym
   * wewnętrznym źródłem prawdy tego komponentu — efekt niżej woła ten
   * callback przy KAŻDEJ jego zmianie niezależnie od przyczyny (klik
   * triggera, klawiatura, klik na zewnątrz, Escape).
   */
  onOpenChange?: (isOpen: boolean) => void;
  /**
   * Pasek „Zaznacz wszystko (N) / Odznacz wszystko (M)" pod polem Szukaj. Akcje działają na
   * WIDOCZNE opcje (`filteredOptions`): po wpisaniu frazy — tylko na wyniki wyszukiwania, przy
   * pustym polu — na całą listę. Wartości zaznaczone, ale nieobecne w `options` (np. dezaktywowany
   * user), zostają nietknięte w obu akcjach. Domyślnie wyłączone — kilkanaście innych konsumentów
   * prymitywu nie zmienia się (opt-in).
   */
  showBulkActions?: boolean;
  /**
   * Przyklejona do dołu panelu dopisek informacyjny (np. „Pokazano 100 z 130 użytkowników", gdy
   * konsument ucina listę). Brak = bez dopisku.
   */
  footerNote?: string;
}

export function MultiSelect({
  value,
  onChange,
  options,
  placeholder,
  label,
  required,
  disabled,
  className,
  valueClassName,
  'data-testid': testIdProp,
  onOpenChange,
  showBulkActions,
  footerNote,
}: MultiSelectProps) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const wrapperRef = useRef<HTMLDivElement>(null);
  // Trigger (`role="combobox"`) — cel zwrotu fokusu po zamknięciu listy klawiszem Esc.
  const comboboxRef = useRef<HTMLDivElement>(null);
  // Panel dropdownu renderowany PORTALEM do body (position: fixed) — inaczej `absolute`
  // panel jest PRZYCINANY przez `overflow` kontenera-rodzica (np. modal ze scrollem):
  // dropdown chował się pod treść modala zamiast wyskakiwać na wierzch. Fixed + portal
  // wychodzi ponad modal. Pozycję liczymy z prostokąta triggera (wrapperRef).
  const panelRef = useRef<HTMLDivElement>(null);
  // Pole Szukaj — po akcji zbiorczej wraca na nie fokus (przycisk, który właśnie się
  // dezaktywował, gubiłby go na `body`).
  const searchRef = useRef<HTMLInputElement>(null);
  const [menuPos, setMenuPos] = useState<AnchoredPanelPosition | null>(null);
  const inputId = useId();
  const listboxId = useId();
  const testId = testIdProp ?? 'multi-select';

  // Notify the caller of every open-state transition — see `onOpenChange` doc
  // above for why this replaces focus/blur bubbling for fetch-on-open gating.
  // Callback captured through a ref so the effect depends ONLY on `isOpen`
  // (callers typically pass an inline/unstable-identity function; we don't
  // want that to re-fire the effect on every unrelated parent render).
  const onOpenChangeRef = useRef(onOpenChange);
  onOpenChangeRef.current = onOpenChange;
  useEffect(() => {
    onOpenChangeRef.current?.(isOpen);
  }, [isOpen]);

  const valueSet = useMemo(() => new Set(value), [value]);
  const normalizedSearch = search ? normalizeSearch(search) : '';
  const filteredOptions = normalizedSearch
    ? options.filter(
        (opt) =>
          normalizeSearch(opt.label).includes(normalizedSearch) ||
          (opt.description && normalizeSearch(opt.description).includes(normalizedSearch)),
      )
    : options;

  const selectedOptions = options.filter((opt) => valueSet.has(opt.value));

  const handleToggle = useCallback(
    (optValue: string) => {
      if (valueSet.has(optValue)) {
        onChange(value.filter((v) => v !== optValue));
      } else {
        onChange([...value, optValue]);
      }
    },
    [onChange, value, valueSet],
  );

  const handleRemove = useCallback(
    (optValue: string) => {
      onChange(value.filter((v) => v !== optValue));
    },
    [onChange, value],
  );

  // Akcje zbiorcze — wyłącznie na widocznych opcjach (`filteredOptions`). Zaznaczone, ale
  // niewidoczne wartości (spoza `options`) nigdy nie są dotykane: ani dopisywane, ani zdejmowane.
  // Licznik liczony tylko przy włączonym pasku — pozostali konsumenci prymitywu nie płacą za niego.
  const visibleSelectedCount = showBulkActions
    ? filteredOptions.filter((opt) => valueSet.has(opt.value)).length
    : 0;

  const handleSelectAllVisible = useCallback(() => {
    const toAdd = filteredOptions.filter((opt) => !valueSet.has(opt.value)).map((opt) => opt.value);
    if (toAdd.length > 0) onChange([...value, ...toAdd]);
    searchRef.current?.focus();
  }, [filteredOptions, onChange, value, valueSet]);

  const handleDeselectAllVisible = useCallback(() => {
    const visible = new Set(filteredOptions.map((opt) => opt.value));
    onChange(value.filter((v) => !visible.has(v)));
    searchRef.current?.focus();
  }, [filteredOptions, onChange, value]);

  // Pozycja portalowanego panelu — liczona z prostokąta triggera; odświeżana przy
  // scrollu/resize (panel `fixed` nie jedzie z treścią, więc trzeba go przesuwać).
  useEffect(() => {
    if (!isOpen) {
      setMenuPos(null);
      return;
    }
    const update = () => {
      const el = wrapperRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      setMenuPos(computeAnchoredPanelPosition(r));
    };
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [isOpen]);

  // Close on click outside — listener registered tylko gdy dropdown otwarty. UWAGA:
  // panel jest w PORTALU (poza wrapperRef), więc klik w opcję liczyłby się jako „na
  // zewnątrz" i zamykał dropdown przed rejestracją kliknięcia — dlatego wykluczamy też
  // panelRef.
  useEffect(() => {
    if (!isOpen) return;
    function handleClickOutside(event: MouseEvent) {
      const target = event.target as globalThis.Node;
      const inWrapper = wrapperRef.current?.contains(target) ?? false;
      const inPanel = panelRef.current?.contains(target) ?? false;
      if (!inWrapper && !inPanel) {
        setIsOpen(false);
        setSearch('');
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Close on Escape — listener tylko gdy dropdown otwarty. Faza przechwytywania + `preventDefault`:
  // okno nakładkowe (`Overlay`) widzi wtedy Esc jako już obsłużony i nie zamyka się razem z listą
  // (ani nie pyta o porzucenie zmian). Fokus wraca na trigger: lista jest w portalu, a fokus był
  // w jej polu wyszukiwania — po jej zniknięciu spadłby na `<body>`, kolejny Esc nie dotarłby do
  // okna, a użytkownik klawiatury straciłby miejsce. (Zamknięcie kliknięciem poza listą fokusu nie
  // rusza — zostaje tam, gdzie kliknął użytkownik.)
  useEffect(() => {
    if (!isOpen) return;
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        event.preventDefault();
        setIsOpen(false);
        setSearch('');
        comboboxRef.current?.focus();
      }
    }
    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [isOpen]);

  const handleTriggerKey = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (disabled) return;
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        setIsOpen((v) => !v);
      }
    },
    [disabled],
  );

  const resolvedPlaceholder = placeholder ?? t('common.multiSelect.placeholder');

  const valueContent =
    selectedOptions.length === 0 ? (
      <span className="text-sm text-text-secondary">{resolvedPlaceholder}</span>
    ) : (
      selectedOptions.map((opt) => (
        <span
          key={opt.value}
          className="flex items-center gap-1 rounded bg-accent-100 px-2 py-0.5 text-xs text-accent-700 dark:bg-accent-900/40 dark:text-accent-300"
          data-testid={`${testId}-chip-${opt.value}`}
        >
          {opt.color && (
            <span
              className="inline-block h-2 w-2 shrink-0 rounded-full"
              style={{ backgroundColor: opt.color }}
              aria-hidden="true"
            />
          )}
          {opt.label}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              handleRemove(opt.value);
            }}
            onKeyDown={(e) => e.stopPropagation()}
            disabled={disabled}
            aria-label={t('common.multiSelect.removeAriaLabel', { label: opt.label })}
            className="inline-flex h-3 w-3 items-center justify-center hover:text-red-500"
            data-testid={`${testId}-chip-remove-${opt.value}`}
          >
            <X className="h-3 w-3" aria-hidden="true" />
          </button>
        </span>
      ))
    );

  return (
    <div ref={wrapperRef} className={`relative ${className ?? ''}`} data-testid={testId}>
      {label && (
        <label htmlFor={inputId} className="mb-1 block text-sm font-medium text-text-primary">
          {label}
          {required && <span className="ml-0.5 text-red-500">*</span>}
        </label>
      )}
      {/*
       * a11y: outer to combobox div (NIE button) — chipy w środku mają własny
       * X button, nie da się zagnieżdżać interactive elementów w button.
       * aria-controls linkuje trigger ↔ listbox (WAI-ARIA 1.2 combobox pattern).
       */}
      <div
        ref={comboboxRef}
        id={inputId}
        role="combobox"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-controls={isOpen ? listboxId : undefined}
        aria-disabled={disabled}
        aria-required={required || undefined}
        tabIndex={disabled ? -1 : 0}
        onClick={() => !disabled && setIsOpen((v) => !v)}
        onKeyDown={handleTriggerKey}
        className={`flex min-h-[42px] w-full cursor-pointer flex-wrap items-center gap-1 rounded-lg border border-border-primary bg-bg-primary px-2 py-1.5 text-left transition-colors hover:border-accent-300 focus:border-accent-500 focus:outline-none focus:ring-2 focus:ring-accent-200 dark:focus:ring-accent-800 ${
          disabled ? 'cursor-not-allowed opacity-50' : ''
        } ${isOpen ? 'border-accent-500' : ''}`}
        data-testid={`${testId}-button`}
      >
        {valueClassName ? (
          <div
            className={cn('flex min-w-0 flex-1 flex-wrap items-center gap-1', valueClassName)}
            data-testid={`${testId}-value`}
          >
            {valueContent}
          </div>
        ) : (
          valueContent
        )}
        <ChevronDown
          className={`ml-auto h-4 w-4 text-text-secondary transition-transform ${
            isOpen ? 'rotate-180' : ''
          }`}
          aria-hidden="true"
        />
      </div>

      {isOpen &&
        menuPos &&
        createPortal(
          <div
            ref={panelRef}
            style={{
              position: 'fixed',
              // Przy odbiciu w górę kotwiczymy DOLNĄ krawędź tuż nad triggerem (bottom), więc krótki
              // panel siedzi przy triggerze zamiast unosić się w górę; w pozostałych przypadkach
              // kotwiczymy górę (top) jak dotąd.
              ...(menuPos.placement === 'above' && menuPos.anchorBottom != null
                ? { bottom: menuPos.anchorBottom }
                : { top: menuPos.top }),
              left: menuPos.left,
              width: menuPos.width,
              // Wysokość docięta do miejsca w oknie (panel scrolluje w środku) — zamiast
              // twardego `max-h-64`, który przy polu na dole formularza wychodził za ekran.
              maxHeight: menuPos.maxHeight,
              zIndex: 60,
            }}
            className="overflow-y-auto rounded-lg border border-border-primary bg-bg-primary shadow-lg"
            // Panel żyje w portalu poza panelem właściwości kreatora — ten atrybut mówi bramce
            // skrótów klawiszowych kreatora (`BUILDER_POPUP_SELECTOR`), że Delete / Backspace / Esc
            // z jego wnętrza (np. z przycisku akcji zbiorczej, który nie leży w `role="listbox"`)
            // nie są jej zdarzeniami.
            data-popup-surface
            data-testid={`${testId}-dropdown`}
          >
            {/*
             * Search input poza role="listbox" (WAI-ARIA APG: listbox akceptuje
             * tylko option/group children). Czytniki ekranu prawidłowo rozpoznają
             * input jako searchbox + listbox jako kolekcję opcji. Szukaj + (opcjonalny)
             * pasek akcji zbiorczych siedzą w JEDNYM przyklejonym kontenerze — oba zostają
             * na górze przy scrollu listy, bez zgadywania wysokości inputa.
             */}
            <div className="sticky top-0 bg-bg-primary">
              <input
                ref={searchRef}
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t('common.multiSelect.searchPlaceholder')}
                aria-label={t('common.multiSelect.searchPlaceholder')}
                aria-controls={listboxId}
                className="w-full border-b border-border-primary bg-bg-primary px-3 py-2 text-sm focus:outline-none"
                autoFocus
                data-testid={`${testId}-search`}
              />
              {showBulkActions && (
                <div
                  className="flex flex-wrap items-center gap-x-2 gap-y-1 border-b border-border-primary px-3 py-1.5"
                  data-testid={`${testId}-bulk-actions`}
                >
                  <Button
                    variant="tertiary"
                    size="sm"
                    className="whitespace-nowrap"
                    onClick={handleSelectAllVisible}
                    disabled={
                      filteredOptions.length === 0 ||
                      visibleSelectedCount === filteredOptions.length
                    }
                    data-testid={`${testId}-select-all`}
                  >
                    {t('common.multiSelect.selectAll', { visibleCount: filteredOptions.length })}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="whitespace-nowrap"
                    onClick={handleDeselectAllVisible}
                    disabled={visibleSelectedCount === 0}
                    data-testid={`${testId}-deselect-all`}
                  >
                    {t('common.multiSelect.deselectAll', { selectedCount: visibleSelectedCount })}
                  </Button>
                </div>
              )}
            </div>
            <div
              id={listboxId}
              role="listbox"
              aria-multiselectable="true"
              aria-label={label ?? t('common.multiSelect.placeholder')}
            >
              {filteredOptions.length === 0 ? (
                <div className="px-3 py-2 text-sm text-text-secondary">
                  {t('common.multiSelect.noResults')}
                </div>
              ) : (
                filteredOptions.map((opt) => {
                  const isSelected = valueSet.has(opt.value);
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      role="option"
                      aria-selected={isSelected}
                      onClick={() => handleToggle(opt.value)}
                      className={`flex w-full items-start gap-2 px-3 py-2 text-left text-sm hover:bg-bg-secondary ${
                        isSelected ? 'bg-accent-50 dark:bg-accent-900/20' : ''
                      }`}
                      data-testid={`${testId}-option-${opt.value}`}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        readOnly
                        aria-hidden="true"
                        tabIndex={-1}
                        className="mt-0.5 h-4 w-4 text-accent-600"
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-1.5 text-text-primary">
                          {opt.color && (
                            <span
                              className="inline-block h-2 w-2 shrink-0 rounded-full"
                              style={{ backgroundColor: opt.color }}
                              aria-hidden="true"
                            />
                          )}
                          <span>{opt.label}</span>
                        </div>
                        {opt.description && (
                          <div className="text-xs text-text-secondary">{opt.description}</div>
                        )}
                      </div>
                    </button>
                  );
                })
              )}
            </div>
            {footerNote && (
              <div
                className="sticky bottom-0 border-t border-border-primary bg-bg-primary px-3 py-2 text-xs text-text-secondary"
                data-testid={`${testId}-footer-note`}
              >
                {footerNote}
              </div>
            )}
          </div>,
          document.body,
        )}
    </div>
  );
}
